/**
 * A value kept in localStorage, shaped for `useSyncExternalStore` (web plan
 * W4.2): the session (`@AuthData`) and the theme (`theme`).
 *
 * - `getServerSnapshot()` is a fixed value (a guest, 'system'): the server has
 *   no storage, and React also uses it while it hydrates the server's HTML, so
 *   the hydrated tree matches that HTML and re-renders with the stored value
 *   right after.
 * - `getSnapshot()` reads storage once, on first use, then answers from memory:
 *   a stable snapshot (React compares it with `Object.is`), and a tab whose
 *   storage is blocked or full still keeps what `set()` gave it.
 * - `set()` updates memory, writes storage (failures are the caller's `write`'s
 *   to swallow) and re-renders subscribers.
 * - `subscribe()` also follows the key's `storage` events, which fire in every
 *   OTHER tab of the site: a login, logout or theme change there shows here.
 *
 * Nothing here runs on import, and nothing touches `window` until a browser
 * render asks for the client snapshot.
 */
export interface StorageStore<T> {
  subscribe: (onChange: () => void) => () => void;
  getSnapshot: () => T;
  getServerSnapshot: () => T;
  set: (next: T | ((current: T) => T)) => void;
}

export interface StorageStoreOptions<T> {
  /** The localStorage key whose `storage` events re-read the value. */
  key: string;
  /** Read and parse the stored value; must not throw (blocked storage → a default). */
  read: () => T;
  /** Persist a value; must not throw (full or blocked storage keeps it in memory only). */
  write: (value: T) => void;
  /** What the server, and the hydration render, see. */
  serverSnapshot: T;
}

export function createStorageStore<T>({
  key,
  read,
  write,
  serverSnapshot,
}: StorageStoreOptions<T>): StorageStore<T> {
  let loaded = false;
  let current: T = serverSnapshot;
  const listeners = new Set<() => void>();

  const getSnapshot = (): T => {
    if (!loaded) {
      current = read();
      loaded = true;
    }
    return current;
  };

  const notify = () => {
    for (const listener of [...listeners]) listener();
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== key) return;
    current = read();
    loaded = true;
    notify();
  };

  return {
    subscribe(onChange) {
      listeners.add(onChange);
      if (listeners.size === 1) window.addEventListener('storage', onStorage);
      return () => {
        listeners.delete(onChange);
        if (listeners.size === 0) window.removeEventListener('storage', onStorage);
      };
    },
    getSnapshot,
    getServerSnapshot: () => serverSnapshot,
    set(next) {
      const previous = getSnapshot();
      const value = typeof next === 'function' ? (next as (current: T) => T)(previous) : next;
      // An updater that keeps the value (e.g. `updateUser` for a guest) writes nothing.
      if (Object.is(value, previous)) return;
      current = value;
      loaded = true;
      write(value);
      notify();
    },
  };
}
